import QRCode from 'qrcode';

// Encode business profile ID for QR code
export const encodeBusinessId = (businessId) => {
  try {
    // Add a prefix and encode with Base64
    const prefixedId = `biz_${businessId}_${Date.now()}`;
    return btoa(prefixedId);
  } catch (error) {
    console.error('Error encoding business ID:', error);
    throw new Error('Failed to encode business ID');
  }
};

// Decode business profile ID from QR token
export const decodeBusinessId = (encodedToken) => {
  try {
    const decoded = atob(encodedToken);
    // Extract business ID from prefixed format: biz_{businessId}_{timestamp}
    const match = decoded.match(/^biz_([a-f0-9]{24})_\d+$/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Error decoding business token:', error);
    return null;
  }
};

// Generate a simple token for QR code access
export const generateQRToken = () => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `business-reg-${timestamp}-${randomStr}`;
};

// Generate QR code for business registration
export const generateBusinessRegistrationQR = async (businessId) => {
  try {
    // Encode the business ID
    const encodedToken = encodeBusinessId(businessId);
    const baseURL = window.location.origin;
    const url = `${baseURL}/external-business-registration/${encodedToken}`;
    
    // Generate QR code as data URL
    const qrDataURL = await QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      quality: 0.92,
      margin: 1,
      width: 256,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    });
    
    return {
      url,
      qrCodeDataURL: qrDataURL,
      token: encodedToken,
      originalBusinessId: businessId,
      generatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('QR Code generation error:', error);
    throw error;
  }
};

// Generate QR code as SVG string
export const generateQRCodeSVG = async (url, options = {}) => {
  try {
    const svgString = await QRCode.toString(url, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 256,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
      ...options
    });
    
    return svgString;
  } catch (error) {
    console.error('SVG QR Code generation error:', error);
    throw error;
  }
};

// Validate if a token is in the correct format (basic validation)
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== 'string') return false;
  
  // Check if token matches our pattern: business-reg-{timestamp}-{random}
  const tokenPattern = /^business-reg-\d+-[a-z0-9]+$/;
  return tokenPattern.test(token);
};

// Generate multiple QR codes for batch operations
export const generateMultipleQRCodes = async (count = 1, prefix = 'batch') => {
  const qrCodes = [];
  
  for (let i = 0; i < count; i++) {
    try {
      const token = `${prefix}-${generateQRToken()}`;
      const qrData = await generateBusinessRegistrationQR(token);
      qrCodes.push({
        ...qrData,
        index: i + 1,
        batchId: prefix
      });
    } catch (error) {
      console.error(`Error generating QR code ${i + 1}:`, error);
    }
  }
  
  return qrCodes;
};

// Download QR code as image file
export const downloadQRCode = (qrDataURL, filename = 'business-registration-qr.png') => {
  const link = document.createElement('a');
  link.href = qrDataURL;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Copy QR code URL to clipboard
export const copyQRUrlToClipboard = async (url) => {
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Failed to copy URL to clipboard:', error);
    return false;
  }
};